import React from 'react';
import { eTechIntel } from '../../mock';
import { ArrowUpRight, Globe, TrendingUp, Zap, FileText } from 'lucide-react';

export default function EtechIntel() {
  return (
    <section className="py-20 lg:py-32 bg-[#0A1530] text-[#F1EDE4] border-t border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-8 mb-12 lg:mb-16 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-[#4ECDC4] mb-4">
              <span className="w-6 h-px bg-[#4ECDC4]" />
              <span>Flagship Media Property</span>
            </div>
            <h2 className="text-4xl lg:text-6xl xl:text-7xl tracking-tight leading-[1.02] font-medium">
              eTechIntel <span className="font-serif italic font-normal text-[#4ECDC4]">— the tech lead engine.</span>
            </h2>
            <p className="mt-6 text-white/70 text-lg max-w-2xl">{eTechIntel.sub}</p>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <a href="https://www.etechintel.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-white/20 pl-5 pr-4 py-3 rounded-full text-sm hover:border-[#4ECDC4] hover:text-[#4ECDC4] transition-colors">
              Visit etechintel.com
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 mb-12">
          {eTechIntel.metrics.map((m, i) => (
            <div key={m.label} className="bg-[#0A1530] p-6 lg:p-8">
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-3">{m.label}</div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl lg:text-5xl font-serif italic tabular text-[#F1EDE4]">{m.value}</span>
                {i === 0 && <TrendingUp className="w-5 h-5 text-[#4ECDC4]" />}
              </div>
              <div className="text-xs text-[#4ECDC4] font-mono mt-2">↑ {m.trend}</div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Pillars */}
          <div className="lg:col-span-8 space-y-6">
            {/* Browser mockup showing eTechIntel */}
            <div className="relative bg-[#0F1C40] border border-white/10 rounded-2xl overflow-hidden">
              {/* browser chrome */}
              <div className="flex items-center gap-2 px-4 py-3 bg-[#050A1F] border-b border-white/10">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
                </div>
                <div className="flex-1 mx-4 h-6 bg-white/5 border border-white/10 rounded-md px-3 flex items-center gap-2 text-[10px] font-mono">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#4ECDC4]" />
                  <span className="text-[#4ECDC4]">etechintel.com</span>
                  <span className="text-white/30">/insights</span>
                </div>
              </div>
              {/* page hero */}
              <div className="p-6 lg:p-8">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-[#4ECDC4] flex items-center justify-center text-[#0F1C40] text-[13px] font-bold">
                      e
                    </div>
                    <div className="text-[13px] font-semibold">eTechIntel</div>
                  </div>
                  <div className="hidden sm:flex items-center gap-4 text-[11px] text-white/60">
                    <span>Insights</span>
                    <span>Research</span>
                    <span>Webinars</span>
                    <span>Community</span>
                    <button className="text-[10px] font-mono uppercase tracking-widest bg-[#4ECDC4] text-[#0F1C40] px-3 py-1.5 rounded-full">
                      Subscribe
                    </button>
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  {/* Featured article */}
                  <div className="md:col-span-2 relative rounded-lg overflow-hidden h-40 md:h-56">
                    <img
                      src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1400&q=80"
                      alt=""
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover opacity-60"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F1C40] via-[#0F1C40]/40 to-transparent" />
                    <div className="absolute inset-0 p-4 flex flex-col justify-end">
                      <div className="text-[9px] font-mono uppercase tracking-widest text-[#FFB020] mb-1">
                        Featured · Whitepaper
                      </div>
                      <div className="text-lg font-serif italic leading-tight">
                        The CIO Playbook 2026: GenAI in Enterprise
                      </div>
                      <div className="text-[11px] text-white/60 mt-1">2,341 downloads · gated</div>
                    </div>
                  </div>
                  {/* Article list */}
                  <div className="space-y-2">
                    {[
                      { t: 'Zero Trust — Live Panel', d: '1,412', c: '#4ECDC4' },
                      { t: 'Post-Cloud Cost Guide', d: '1,876', c: '#4ECDC4' },
                      { t: 'AI-native Data Platforms', d: '1,204', c: '#FFB020' },
                    ].map((a) => (
                      <div
                        key={a.t}
                        className="bg-white/5 border border-white/10 rounded-lg p-3 hover:bg-white/10 transition-colors"
                      >
                        <div className="text-[12px] leading-tight">{a.t}</div>
                        <div className="text-[9px] font-mono uppercase text-white/40 mt-1" style={{ color: a.c }}>
                          {a.d} downloads
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* subscribe strip */}
                <div className="mt-5 pt-5 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
                  <div className="text-[11px] text-white/60">
                    <span className="font-mono uppercase tracking-widest text-[#4ECDC4] text-[10px]">
                      3.1M subscribers
                    </span>
                    <span className="mx-2 text-white/30">·</span>
                    Get the weekly Tech Intel briefing.
                  </div>
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <div className="flex-1 sm:w-52 h-8 bg-white/5 border border-white/10 rounded-md px-3 flex items-center text-[11px] text-white/40">
                      you@company.com
                    </div>
                    <button className="text-[10px] font-mono uppercase tracking-widest bg-[#FFB020] text-[#0F1C40] px-3 py-1.5 rounded-md">
                      Subscribe
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {eTechIntel.pillars.map((p) => (
                <div
                  key={p.n}
                  className="bg-[#12234C] border border-white/10 rounded-2xl p-6 hover:border-[#4ECDC4] transition-colors reveal"
                >
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#4ECDC4]">{p.n}</span>
                    <span className="font-mono text-xs text-white/40">{p.touchpoints} touchpoints</span>
                  </div>
                  <h3 className="text-xl font-serif italic font-normal mb-3">{p.title}</h3>
                  <p className="text-[13px] text-white/70 leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Geo + content sample */}
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-[#12234C] border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#4ECDC4] mb-6">
                <Globe className="w-3.5 h-3.5" />
                <span>Top markets · last 7 days</span>
              </div>
              <div className="space-y-3">
                {eTechIntel.markets.map((m) => (
                  <div key={m.name}>
                    <div className="flex items-baseline justify-between mb-1.5">
                      <span className="text-sm">{m.name}</span>
                      <span className="font-mono text-xs text-white/50">{m.requests}</span>
                    </div>
                    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-[#4ECDC4]" style={{ width: `${m.pct * 2}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#12234C] border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#4ECDC4] mb-4">
                <FileText className="w-3.5 h-3.5" />
                <span>Recent gated content</span>
              </div>
              <ul className="space-y-3">
                {eTechIntel.contentSample.map((c) => (
                  <li key={c.title} className="flex items-start justify-between gap-3 pb-3 border-b border-white/5 last:border-0 last:pb-0">
                    <div>
                      <div className="text-[13px] leading-snug">{c.title}</div>
                      <div className="text-[10px] font-mono uppercase text-white/40 mt-1">{c.type}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono text-sm tabular">{c.downloads}</div>
                      <div className="text-[9px] text-white/40 uppercase">downloads</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-br from-[#4ECDC4]/20 to-transparent border border-[#4ECDC4]/30 rounded-2xl p-6">
              <Zap className="w-5 h-5 text-[#4ECDC4] mb-3" />
              <div className="text-[11px] font-mono uppercase tracking-wider text-[#4ECDC4] mb-1">Community</div>
              <div className="text-2xl font-serif italic">150K+ forum posts</div>
              <div className="text-xs text-white/60 mt-1">Sustained engagement drives repeat visits.</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
